import { Injectable } from "@angular/core";
import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoUserPoolData,
} from "amazon-cognito-identity-js";
import { catchError, from, map, Observable, of } from "rxjs";
import { UserIdentifiers } from "../interfaces/auth.interfaces";
import { SessionExpiredError } from "../errors/session-expired.error";
import { UserRole } from "../../core/interfaces/user.interface";
import { environment } from "../../../environments/environment";

/** Role names known to the app, used to drop unknown Cognito groups. */
const KNOWN_ROLES = new Set<string>(Object.values(UserRole));

@Injectable({
  providedIn: "root",
})
export class CognitoUtilsService {
  getUserPool(): CognitoUserPool {
    const userPoolData: ICognitoUserPoolData = {
      UserPoolId: environment.cognito.userPoolId,
      ClientId: environment.cognito.userPoolClientId,
    };
    return new CognitoUserPool(userPoolData);
  }

  getCurrentUser(): CognitoUser | null {
    return this.getUserPool().getCurrentUser();
  }

  /**
   * The currently in-flight session lookup, shared between concurrent callers.
   * Cleared once it settles so later calls perform a fresh check.
   */
  private _sessionInFlight: Promise<CognitoUserSession | null> | null = null;

  /**
   * Checks the user's session.
   *
   * Concurrent calls are de-duplicated: during bootstrap `getSession()` is
   * invoked several times (AuthService constructor + each canMatch guard +
   * other callers), and amazon-cognito-identity-js fires a fresh token-refresh
   * request every time. Sharing a single in-flight promise collapses that burst
   * into one refresh attempt — avoiding a stack of redundant requests (and a
   * pile of 400s when the refresh token is expired), and sidestepping the
   * refresh-token rotation issues that concurrent refreshes can cause.
   */
  async getSession(): Promise<CognitoUserSession | null> {
    if (!this._sessionInFlight) {
      this._sessionInFlight = this._loadSession();
      // Clear the shared promise once it settles so the next call re-checks.
      void this._sessionInFlight
        .catch(() => undefined)
        .finally(() => {
          this._sessionInFlight = null;
        });
    }
    return this._sessionInFlight;
  }

  /**
   * Performs the actual Cognito session lookup (and silent token refresh when
   * needed). Resolves with `null` when there is no current user.
   */
  private _loadSession(): Promise<CognitoUserSession | null> {
    const cognitoUser = this.getCurrentUser();
    if (!cognitoUser) {
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession(
        (error: Error | null, session: CognitoUserSession | null) => {
          if (error) {
            reject(error);
          } else {
            resolve(session);
          }
        },
      );
    });
  }

  /**
   * Get user identifiers from Cognito token as Observable
   * @returns Observable with tenant, sub and group
   */
  getUserIdentifiers(): Observable<UserIdentifiers> {
    return from(this.getSession()).pipe(
      map((session) => {
        if (!session) {
          throw new Error("CognitoUserSession null");
        }
        const authToken = session.getIdToken().decodePayload();

        const payload = authToken as {
          "custom:tenant"?: string;
          sub: string;
          "cognito:groups"?: string[];
        };
        const tenant = payload["custom:tenant"];
        const { sub } = payload;

        return {
          tenant: tenant ?? "",
          sub,
          groups: payload["cognito:groups"] ?? [],
        };
      }),
      catchError((error: unknown) => {
        // Preserve the explicit "no current user" sentinel.
        if (error instanceof Error && error.message === "CognitoUserSession null") {
          throw error;
        }
        // getSession() rejected: the refresh token is expired/invalid, so the
        // session is dead. Surface a dedicated error so callers can tell it
        // apart from unrelated failures (HTTP errors, RxJS timeouts, ...).
        throw new SessionExpiredError(error);
      }),
    );
  }

  /**
   * Normalizes raw Cognito groups into the app's known roles: upper-cased, with
   * unknown groups dropped. The single place a `cognito:groups` value becomes a
   * {@link UserRole} — reused by {@link getUserRoles} (async, from the session)
   * and by callers that already hold the groups from a token read (e.g.
   * LoggedUserService merges these onto the user it loads from Mongo).
   *
   * @param groups - Raw group names from the `cognito:groups` claim.
   * @returns The known roles, upper-cased; unknown groups are dropped.
   */
  rolesFromGroups(groups: string[]): UserRole[] {
    return groups
      .map((group) => group.toUpperCase())
      .filter((group): group is UserRole => KNOWN_ROLES.has(group));
  }

  /**
   * The user's roles, resolved from the Cognito `cognito:groups` claim — the
   * security-relevant entry point the route guards call into. Delegates the
   * group-to-role normalization to {@link rolesFromGroups}; a missing/expired
   * session yields no roles (fail closed to the restrictive default rather than
   * throwing).
   */
  getUserRoles(): Observable<UserRole[]> {
    return this.getUserIdentifiers().pipe(
      map(({ groups }) => this.rolesFromGroups(groups)),
      catchError(() => of([] as UserRole[])),
    );
  }
}
